import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Users, Plus, Copy, Mail, Trash2, Crown, UserPlus, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Label } from './ui/label';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import api from '../utils/api';

const FamilySection = () => {
  const { t } = useTranslation();
  const [families, setFamilies] = useState([]);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [members, setMembers] = useState({});
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [familyName, setFamilyName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showJoinDialog, setShowJoinDialog] = useState(false);

  useEffect(() => {
    fetchFamilies();
  }, []);

  const fetchFamilies = async () => {
    try {
      setLoading(true);
      const response = await api.get('/family/');
      if (response.data.families) {
        setFamilies(response.data.families);
        // Fetch members for each family
        response.data.families.forEach(family => {
          fetchMembers(family.id);
        });
      }
    } catch (error) {
      console.error('Error fetching families:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMembers = async (familyId) => {
    try {
      const response = await api.get(`/family/members/${familyId}`);
      setMembers(prev => ({ ...prev, [familyId]: response.data.members || [] }));
    } catch (error) {
      console.error('Error fetching members:', error);
    }
  };

  const handleCreateFamily = async () => {
    if (!familyName.trim()) {
      toast.error(t('common.error'));
      return;
    }

    setCreating(true);
    try {
      const response = await api.post('/family/create', { name: familyName });
      toast.success(t('family.familyCreated'));
      setShowCreateDialog(false);
      setFamilyName('');
      fetchFamilies();
    } catch (error) {
      console.error('Create family error:', error);
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setCreating(false);
    }
  };

  const handleInviteByEmail = async (familyId) => {
    if (!inviteEmail.trim()) {
      toast.error(t('common.error'));
      return;
    }

    setInviting(true);
    try {
      const response = await api.post(`/family/invite/email/${familyId}`, { email: inviteEmail });
      toast.success(t('family.memberAddedSuccessfully'));
      toast.info(`${t('family.inviteCode')}: ${response.data.invite_code}`, { duration: 5000 });
      setInviteEmail('');
      setShowInviteDialog(false);
    } catch (error) {
      console.error('Invite error:', error);
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setInviting(false);
    }
  };

  const handleJoinFamily = async () => {
    if (!joinCode.trim()) {
      toast.error(t('common.error'));
      return;
    }

    try {
      const response = await api.post('/family/join', { invite_code: joinCode });
      toast.success(t('family.joinedFamily'));
      setShowJoinDialog(false);
      setJoinCode('');
      fetchFamilies();
    } catch (error) {
      console.error('Join error:', error);
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(t('family.codeCopied'));
  };

  const handleRegenerateCode = async (familyId) => {
    try {
      await api.post(`/family/regenerate-code/${familyId}`);
      toast.success(t('family.codeRegenerated'));
      fetchFamilies();
    } catch (error) {
      toast.error(t('family.errorRegeneratingCode'));
    }
  };

  const handleLeaveFamily = async (familyId, familyName) => {
    if (!window.confirm(`${t('family.confirmLeave')} "${familyName}"?`)) return;

    try {
      await api.delete(`/family/leave/${familyId}`);
      toast.success(t('family.leftFamily'));
      fetchFamilies();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('family.errorLeavingFamily'));
    }
  };

  const handleRemoveMember = async (familyId, memberId) => {
    if (!window.confirm(t('family.removeMember'))) return;

    try {
      await api.delete(`/family/members/${familyId}/${memberId}`);
      toast.success(t('family.memberRemoved'));
      fetchMembers(familyId);
    } catch (error) {
      toast.error(t('family.errorRemovingMember'));
    }
  };

  if (loading) {
    return (
      <Card className="p-6 border-2 border-amber-100">
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-orange-600 border-t-transparent"></div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 border-2 border-amber-100">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-2xl font-semibold text-amber-900 flex items-center gap-2">
          <Users className="w-6 h-6" />
          {t('family.myFamilies')} ({families.length})
        </h3>
        <div className="flex flex-col sm:flex-row gap-2">
          <Dialog open={showJoinDialog} onOpenChange={setShowJoinDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-orange-300 w-full sm:w-auto text-sm">
                <UserPlus className="w-4 h-4 mr-2" />
                {t('family.joinFamily')}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t('family.enterFamily')}</DialogTitle>
                <DialogDescription>
                  {t('family.enterCode')}
                </DialogDescription>
              </DialogHeader>
              <div className="py-4">
                <Label>{t('family.inviteCode')}</Label>
                <Input
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value)}
                  placeholder={t('family.inviteCodePlaceholder')}
                  className="mt-2"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowJoinDialog(false)}>
                  {t('common.cancel')}
                </Button>
                <Button onClick={handleJoinFamily}>
                  {t('family.enter')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="bg-orange-600 hover:bg-orange-700 w-full sm:w-auto text-sm">
                <Plus className="w-4 h-4 mr-2" />
                {t('family.createFamily')}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t('family.createNewFamily')}</DialogTitle>
                <DialogDescription>
                  {t('family.createFamilyDescription')}
                </DialogDescription>
              </DialogHeader>
              <div className="py-4">
                <Label>{t('family.familyName')}</Label>
                <Input
                  value={familyName}
                  onChange={(e) => setFamilyName(e.target.value)}
                  placeholder={t('family.familyNamePlaceholder')}
                  className="mt-2"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                  {t('common.cancel')}
                </Button>
                <Button onClick={handleCreateFamily} disabled={creating}>
                  {creating ? t('family.creating') : t('family.create')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {families.length === 0 ? (
        <div className="text-center py-8">
          <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">{t('family.noFamiliesYet')}</p>
          <p className="text-sm text-gray-500">{t('family.createOrJoin')}</p>
        </div>
      ) : (
        <Accordion type="single" collapsible className="w-full">
          {families.map((family) => (
            <AccordionItem key={family.id} value={family.id}>
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center justify-between w-full pr-4">
                  <div className="flex items-center gap-3">
                    <Users className="w-5 h-5 text-orange-600" />
                    <span className="font-semibold text-lg">{family.name}</span>
                    {family.user_role === 'admin' && (
                      <Badge className="bg-orange-600 text-white">
                        <Crown className="w-3 h-3 mr-1" />
                        {t('family.admin')}
                      </Badge>
                    )}
                  </div>
                  <span className="text-sm text-gray-500">{family.member_count} {t('family.membersCount')}</span>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-4 pt-4">
                  {/* Invite Code */}
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <Label className="text-sm text-gray-700 mb-2 block">{t('family.inviteCode')}</Label>
                    <div className="flex gap-2">
                      <Input
                        value={family.invite_code}
                        readOnly
                        className="font-mono bg-white"
                      />
                      <Button onClick={() => handleCopyCode(family.invite_code)} variant="outline" size="icon">
                        <Copy className="w-4 h-4" />
                      </Button>
                      {family.user_role === 'admin' && (
                        <Button onClick={() => handleRegenerateCode(family.id)} variant="outline" size="icon">
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Invite by Email (Admin only) */}
                  {family.user_role === 'admin' && (
                    <Dialog open={showInviteDialog && selectedFamily === family.id} onOpenChange={(open) => {
                      setShowInviteDialog(open);
                      setSelectedFamily(open ? family.id : null);
                    }}>
                      <DialogTrigger asChild>
                        <Button variant="outline" className="w-full border-orange-300">
                          <Mail className="w-4 h-4 mr-2" />
                          {t('family.inviteByEmail')}
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>{t('family.inviteTo')} {family.name}</DialogTitle>
                          <DialogDescription>
                            {t('family.inviteEmailDescription')}
                          </DialogDescription>
                        </DialogHeader>
                        <div className="py-4">
                          <Label>{t('contact.email')}</Label>
                          <Input
                            type="email"
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                            placeholder={t('family.inviteEmailPlaceholder')}
                            className="mt-2"
                          />
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setShowInviteDialog(false)}>
                            {t('common.cancel')}
                          </Button>
                          <Button onClick={() => handleInviteByEmail(family.id)} disabled={inviting}>
                            {inviting ? t('family.sending') : t('family.sendInvite')}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  )}

                  {/* Members List */}
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      {t('family.members')} ({members[family.id]?.length || 0})
                    </h4>
                    <div className="space-y-2">
                      {members[family.id]?.map((member) => (
                        <div
                          key={member.user_id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            {member.user_picture ? (
                              <img
                                src={member.user_picture}
                                alt={member.user_name}
                                className="w-10 h-10 rounded-full"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-orange-200 flex items-center justify-center">
                                <Users className="w-5 h-5 text-orange-700" />
                              </div>
                            )}
                            <div>
                              <div className="font-medium text-gray-900">{member.user_name}</div>
                              <div className="text-sm text-gray-600">{member.user_email}</div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            {member.role === 'admin' && (
                              <Badge variant="secondary" className="bg-orange-100 text-orange-700">
                                <Crown className="w-3 h-3 mr-1" />
                                {t('family.admin')}
                              </Badge>
                            )}
                            
                            {family.user_role === 'admin' && member.role !== 'admin' && (
                              <Button
                                onClick={() => handleRemoveMember(family.id, member.user_id)}
                                size="icon"
                                variant="ghost"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Leave Family */}
                  <Button
                    onClick={() => handleLeaveFamily(family.id, family.name)}
                    variant="outline"
                    className="w-full border-red-300 text-red-600 hover:bg-red-50"
                  >
                    {t('family.leaveFamily')}
                  </Button>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </Card>
  );
};

export default FamilySection;
